import React, { useEffect, useMemo, useRef, useState } from "react";
import { answersPopup } from "./popup";

/**
 * Punjab (unscramble.php) and Karnataka (match.php): a fill-in-the-answer form.
 * A box turns green and locks when it holds the exact answer; one point each.
 */
const CONFIG = {
  punjab: {
    intro:
      "Take this quiz to know more about this celebration and answer correctly to illuminate the Gurudwara with festive lights",
    questions: [
      "Translation of Bandi Chhor Diwas",
      <>
        It was celebrated due to the release of ___ number of Hindu Kings &amp;
        Princes from Mughal prison in Gwalior. <b>(Answer in numerics)</b>
      </>,
      "The celebrations of the festive season mark the arrival of _____ season in Punjab",
    ],
    answers: ["prisoner release day", "52", "winter"],
    // plain-text versions for the "right answers" popup
    labels: [
      "Translation of Bandi Chhor Diwas",
      "Number of Hindu Kings & Princes released from the Mughal prison in Gwalior",
      "Season whose arrival the festive celebrations mark in Punjab",
    ],
    display: ["Prisoner release day", "52", "Winter"],
  },
  karnataka: {
    questions: ["Ans 1", "Ans 2"],
    answers: ["c", "a"],
    match: {
      left: ["1. Ashwija Krishna Chaturdashi", "2. Bali Padyami"],
      right: [
        "A. Create forts made from cow dung",
        "B. Burn jute sticks to seek blessings",
        "C. Bathe in oil infused with betel leaves",
      ],
    },
  },
};

const norm = (v) => String(v ?? "").trim().toLowerCase();

/** Rows for the right-answers popup: question, right answer, and what the player typed if it was wrong. */
function answerRows(cfg, values) {
  return cfg.answers.map((answer, i) => {
    // Karnataka answers are option letters; show the option text, e.g. "C. Bathe in oil ..."
    const optionText = cfg.match
      ? cfg.match.right.find((r) => r.toLowerCase().startsWith(`${answer}.`))
      : null;
    return {
      label: cfg.labels ? cfg.labels[i] : cfg.match ? cfg.match.left[i] : "",
      answer: optionText || (cfg.display ? cfg.display[i] : answer),
      correct: values ? norm(values[i]) === answer : false,
      yours: values ? String(values[i] ?? "").trim() : "",
    };
  });
}

export default function TypeAnswers({ gameKey, api }) {
  const cfg = CONFIG[gameKey];
  const { setScore, initialState, saveProgress, setReveal } = api;
  // Typed answers and the boxes already locked green come back from the DB after a refresh.
  const [values, setValues] = useState(() =>
    cfg.answers.map((_, i) => String(initialState?.values?.[i] ?? ""))
  );
  const [locked, setLocked] = useState(() =>
    cfg.answers.map((_, i) => !!initialState?.locked?.[i])
  );
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    saveProgress({ values, locked });
  }, [values, locked, saveProgress]);

  const points = useMemo(
    () => values.filter((v, i) => norm(v) === cfg.answers[i]).length,
    [values, cfg.answers]
  );

  useEffect(() => {
    setScore(points);
  }, [points, setScore]);

  const onChange = (i, raw) => {
    if (locked[i]) return;
    setValues((prev) => prev.map((v, k) => (k === i ? raw : v)));
    if (raw.toLowerCase() === cfg.answers[i]) {
      setLocked((prev) => prev.map((l, k) => (k === i ? true : l)));
    }
  };

  // Skip: show every right answer first.
  useEffect(() => {
    setReveal(() => answersPopup({ title: "Right answers", items: answerRows(cfg, null) }));
    return () => setReveal(null);
  }, [setReveal, cfg]);

  const submitBusy = useRef(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (api.saving || submitBusy.current) return;
    submitBusy.current = true;
    // Any wrong answer: show the right answers before the score is saved.
    if (points < cfg.answers.length) {
      await answersPopup({
        title: "Here are the right answers",
        intro: `You got ${points} of ${cfg.answers.length} right.`,
        items: answerRows(cfg, values),
      });
    }
    submitBusy.current = false;
    api.submit(
      points,
      values.map((v) => norm(v))
    );
  };

  const inputClass = (i) => {
    if (locked[i]) return "desi-input is-correct";
    return values[i] ? "desi-input is-wrong" : "desi-input";
  };

  return (
    <>
      {cfg.intro ? <p className="desi-intro">{cfg.intro}</p> : null}

      {cfg.match ? (
        <div className="desi-match">
          <div className="desi-match__col">
            {cfg.match.left.map((t) => (
              <div key={t} className="desi-match__item">
                {t}
              </div>
            ))}
          </div>
          <div className="desi-match__col">
            {cfg.match.right.map((t) => (
              <div key={t} className="desi-match__item">
                {t}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <form
        className={`desi-form${cfg.match ? " desi-form--inline" : ""}`}
        onSubmit={onSubmit}
      >
        <ul className="desi-quiz">
          {cfg.questions.map((q, i) => (
            <li key={i}>
              <span className="desi-quiz__num">{i + 1}</span>
              {q}{" "}
              <input
                type="text"
                className={inputClass(i)}
                value={values[i]}
                readOnly={locked[i]}
                required
                pattern=".*\S.*"
                title="This field cannot be blank or only spaces"
                autoComplete="off"
                onChange={(e) => onChange(i, e.target.value)}
              />
            </li>
          ))}
        </ul>
        <button type="submit" className="desi-submit" disabled={api.saving}>
          Submit
        </button>
      </form>
    </>
  );
}
